import { films } from '@/mocks/films';
import { categories } from '@/mocks/categories';
import type { Film } from '@/types/content';

export const getFilmById = (id: string): Film | undefined => films.find((film) => film.id === id);

export const getCategoryById = (id: string) => categories.find((category) => category.id === id);

export const getCategoryName = (id: string) => getCategoryById(id)?.name ?? '全部内容';

export const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} 分钟`;
  return `${hours} 小时 ${rest} 分`;
};

export const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getFullYear()} 年 ${date.getMonth() + 1} 月 ${date.getDate()} 日`;
};

export type FilmFilters = {
  types?: string[];
  regions?: string[];
  years?: string[];
  keyword?: string;
};

export const filterFilms = (source: Film[], filters: FilmFilters): Film[] => {
  const keyword = (filters.keyword ?? '').trim().toLowerCase();

  return source.filter((film) => {
    const typeMatch = !filters.types?.length || filters.types.includes(film.category);
    const regionMatch = !filters.regions?.length || filters.regions.includes(film.region);
    const yearMatch = !filters.years?.length || filters.years.includes(String(film.year));

    if (!typeMatch || !regionMatch || !yearMatch) return false;
    if (!keyword) return true;

    const haystack = [film.title, film.summary, film.synopsis, film.region, film.director, ...film.tags, ...film.cast]
      .join(' ')
      .toLowerCase();

    return haystack.includes(keyword);
  });
};

export type SortKey = 'latest' | 'popular' | 'rating';

export const sortFilms = (source: Film[], sort: SortKey): Film[] => {
  const list = [...source];
  if (sort === 'popular') return list.sort((a, b) => b.popularity - a.popularity);
  if (sort === 'rating') return list.sort((a, b) => b.rating - a.rating);
  return list.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
};

export const getRecommended = (film: Film, limit = 4): Film[] => {
  const scored = films
    .filter((item) => item.id !== film.id)
    .map((item) => {
      let score = 0;
      if (item.category === film.category) score += 3;
      if (item.subcategory === film.subcategory) score += 2;
      score += item.tags.filter((tag) => film.tags.includes(tag)).length * 2;
      if (item.region === film.region) score += 1;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating);

  return scored.slice(0, limit).map((entry) => entry.item);
};

export const yearOptions = Array.from(new Set(films.map((film) => film.year)))
  .sort((a, b) => b - a)
  .map(String);